import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  Signal,
  viewChild,
} from '@angular/core';
import { Mon, MonMetadata } from '../../generated/mon';
import { FrameRenderData, SpriteAnimationConfig, drawSpriteFrame } from '../../types/animation';
import { FrameLoopService } from '../../services/frame-loop.service';

export type MonSpriteType = keyof Mon['sprites'];

/**
 * Mon-aware sprite for non-battle / static contexts (chips, dropdowns, team
 * preview, facet inventory, gacha reveal, mon info). Looks up
 * `MonMetadata[id].sprites[type]`, registers it with `FrameLoopService`
 * (shared battle-free rAF + spritesheet cache), and draws each frame onto its own canvas.
 * Pass `monId = null` to render an empty placeholder.
 *
 * Draws to its own canvas from an effect (rather than delegating to
 * AnimatedSpriteComponent) so per-frame updates flow straight from the frame
 * signal into the draw. Routing them through a child component's input binding
 * would require a view-CD pass on every frame, which a static OnPush host never
 * reliably gets — that left the opponent-team-viewer minis blank/flaky on load.
 *
 * Distinct from `MonSpriteComponent` (the battle-coupled mon view).
 */
@Component({
  selector: 'app-mon-static-sprite',
  template: `
    @if (config()) {
      <canvas #canvas></canvas>
    } @else {
      <span class="empty"></span>
    }
  `,
  styles: [`
    :host {
      display: inline-block;
      line-height: 0;
      flex: 0 0 auto;
    }
    canvas {
      width: 100%;
      height: 100%;
      display: block;
      image-rendering: pixelated;
    }
    .empty {
      display: inline-block;
      width: 100%;
      height: 100%;
      border: 1px dashed rgba(255, 255, 255, 0.18);
      border-radius: 2px;
      background-color: rgba(0, 0, 0, 0.25);
      box-sizing: border-box;
    }
  `],
  host: {
    '[style.width]': 'displayWidthStyle()',
    '[style.height]': 'displayHeightStyle()',
  },
})
export class MonStaticSpriteComponent implements OnDestroy {
  readonly monId = input.required<number | null>();
  readonly spriteType = input<MonSpriteType>('micro');
  readonly displayWidth = input<number | null>(null);
  readonly displayHeight = input<number | null>(null);
  readonly freezeOnSkip = input<boolean>(false);
  readonly animationComplete = output<void>();

  private readonly animation = inject(FrameLoopService);
  private readonly hostRef = inject(ElementRef<HTMLElement>);
  private readonly frameSource = signal<Signal<FrameRenderData | null> | null>(null);
  private readonly canvasRef = viewChild<ElementRef<HTMLCanvasElement>>('canvas');
  private currentRegistrationId: string | null = null;
  private readonly visible = signal(true);
  private intersection: IntersectionObserver | null = null;

  protected readonly config = computed(() => {
    const id = this.monId();
    if (id === null) return null;
    return (MonMetadata as Record<number, Mon>)[id]?.sprites?.[this.spriteType()] ?? null;
  });

  // Only set explicit px when a display size is provided; otherwise let CSS size the host.
  protected readonly displayWidthStyle = computed(() => {
    const w = this.displayWidth();
    return w !== null ? `${w}px` : null;
  });
  protected readonly displayHeightStyle = computed(() => {
    const h = this.displayHeight();
    return h !== null ? `${h}px` : null;
  });

  constructor() {
    effect(() => {
      const cfg = this.config();
      this.unregister();
      if (!cfg) {
        this.frameSource.set(null);
        return;
      }
      // Chain onComplete so the caller-supplied callback still runs and our
      // (animationComplete) output fires for template-bound handlers.
      const wrapped: SpriteAnimationConfig = {
        ...cfg,
        freezeOnSkip: this.freezeOnSkip(),
        onComplete: () => {
          cfg.onComplete?.();
          this.animationComplete.emit();
        },
      };
      const { id, frame } = this.animation.registerAnimation(wrapped);
      this.currentRegistrationId = id;
      this.frameSource.set(frame);
    });

    // Long lists mount dozens of these; skip draws while scrolled out of view.
    if (typeof IntersectionObserver !== 'undefined') {
      this.intersection = new IntersectionObserver((entries) => {
        this.visible.set(entries[entries.length - 1]?.isIntersecting ?? true);
      });
      this.intersection.observe(this.hostRef.nativeElement);
    }

    // Draw on every frame update. Reads the frame signal directly, so it re-runs
    // on each rAF tick — and sizes the backing store in the same pass — without
    // depending on this (static, OnPush) component's change detection.
    // Visibility is read FIRST: while hidden the frame signal is never read, so
    // per-frame updates don't wake this effect; re-entering view redraws current.
    effect(() => {
      if (!this.visible()) return;
      const src = this.frameSource();
      drawSpriteFrame(this.canvasRef()?.nativeElement, src ? src() : null);
    });
  }

  ngOnDestroy(): void {
    this.intersection?.disconnect();
    this.unregister();
  }

  private unregister(): void {
    if (this.currentRegistrationId) {
      this.animation.unregisterAnimation(this.currentRegistrationId);
      this.currentRegistrationId = null;
    }
  }
}
